// Central database for Maintenance & Reliability Metrics
// Based on Industry Best Practices

export const categories = [
  {
    id: 'business',
    name: 'Business & Management',
    icon: 'FiBriefcase',
    color: 'text-amber-600',
    bg: 'bg-amber-50',
    border: 'border-amber-200'
  },
  {
    id: 'process',
    name: 'Manufacturing Process Reliability',
    icon: 'FiSettings',
    color: 'text-jeruel-blue',
    bg: 'bg-jeruel-soft',
    border: 'border-jeruel-grayblue'
  },
  {
    id: 'equipment',
    name: 'Equipment Reliability',
    icon: 'FiTool',
    color: 'text-emerald-600',
    bg: 'bg-emerald-50',
    border: 'border-emerald-200'
  },
  {
    id: 'leadership',
    name: 'Organization & Leadership',
    icon: 'FiUsers',
    color: 'text-violet-600',
    bg: 'bg-violet-50',
    border: 'border-violet-200'
  },
  {
    id: 'work',
    name: 'Work Management',
    icon: 'FiClipboard',
    color: 'text-rose-600',
    bg: 'bg-rose-50',
    border: 'border-rose-200'
  },
];

export const metrics = [
  // ── BUSINESS & MANAGEMENT ──────────────────────────────────────
  {
    id: "rav-headcount-ratio",
    categoryId: "business",
    name: "RAV to Craft-Wage Headcount",
    fullName: "Ratio of Replacement Asset Value (RAV) to Craft-Wage Headcount",
    definition: "This metric is the replacement asset value (RAV) of the assets being maintained at the plant divided by the craft-wage employee headcount. The result is expressed as a ratio in dollars per craft-wage employee.",
    formula: "RAV ($) ÷ Craft-Wage Headcount",
    formulaDisplay: "RAV / Headcount",
    inputs: [
      { id: "rav", label: "Replacement Asset Value", unit: "$" },
      { id: "headcount", label: "Craft-Wage Headcount", unit: "FTE" },
    ],
    calc: (v) => v.headcount > 0 ? { value: v.rav / v.headcount, label: "$ per Employee", format: "currency" } : null,
    benchmark: { target: "Varies by industry", type: "none" },
    insight: "Best-in-class plants generally have higher asset utilization and equipment reliability, resulting in a higher RAV to craft-wage cost ratio. This metric normalizes for plant size.",
    objectives: "Compare the ratio of craft-wage personnel across sites and benchmark against industry standards."
  },
  {
    id: "maintenance-unit-cost",
    categoryId: "business",
    name: "Maintenance Unit Cost",
    fullName: "Maintenance Unit Cost",
    definition: "This metric is the measure of the total maintenance cost required for an asset or facility to generate a unit of production.",
    formula: "Total Maintenance Cost ($) ÷ Standard Units Produced",
    formulaDisplay: "Cost / Units",
    inputs: [
      { id: "cost", label: "Total Maintenance Cost", unit: "$" },
      { id: "units", label: "Standard Units Produced", unit: "units" },
    ],
    calc: (v) => v.units > 0 ? { value: v.cost / v.units, label: "$ per Unit", format: "currency4" } : null,
    benchmark: { target: "Trend Downward", type: "trend" },
    insight: "Rising unit cost often signals declining asset health or efficiency. This measure can be applied to specific assets, groups, or entire facilities to track cost effectiveness over time.",
    objectives: "Quantify the total maintenance cost to produce a standard unit of production over a specified time period."
  },
  {
    id: "mro-inventory-rav",
    categoryId: "business",
    name: "MRO Inventory % of RAV",
    fullName: "Stocked MRO Inventory Value as % of RAV",
    definition: "Value of stocked MRO materials/parts as a percentage of Replacement Asset Value. Includes spare parts stocked onsite and remotely.",
    formula: "(Stocked MRO Value ($) × 100) ÷ RAV ($)",
    formulaDisplay: "(MRO Value / RAV) × 100",
    inputs: [
      { id: "mro", label: "Stocked MRO Value", unit: "$" },
      { id: "rav", label: "Replacement Asset Value", unit: "$" },
    ],
    calc: (v) => v.rav > 0 ? { value: (v.mro / v.rav) * 100, label: "%", format: "percent2" } : null,
    benchmark: { target: "0.3% – 1.5%", low: 0.3, high: 1.5, type: "range", unit: "%" },
    insight: "Top quartile plants maintain 0.3%–1.5% of RAV in inventory. Caution: This low target is only reasonable if maintenance practices are advanced and mature. Less mature plants may need higher inventory levels.",
    objectives: "Enable comparisons of stocked maintenance inventory value normalized by plant size and value."
  },
  {
    id: "total-maintenance-cost-rav",
    categoryId: "business",
    name: "Total Maintenance Cost % of RAV",
    fullName: "Total Maintenance Cost as % of RAV",
    definition: "The amount of money spent annually maintaining assets, divided by the replacement asset value (RAV) of the assets being maintained, expressed as a percentage.",
    formula: "(Total Maintenance Cost ($) × 100) ÷ RAV ($)",
    formulaDisplay: "(Maint Cost / RAV) × 100",
    inputs: [
      { id: "cost", label: "Total Maintenance Cost", unit: "$" },
      { id: "rav", label: "Replacement Asset Value", unit: "$" },
    ],
    calc: (v) => v.rav > 0 ? { value: (v.cost / v.rav) * 100, label: "%", format: "percent2" } : null,
    benchmark: { target: "2% – 3%", low: 2, high: 3, type: "range", unit: "%" },
    insight: "World-class facilities typically spend <2% of RAV. Caution: Low spending is only sustainable with advanced reliability practices. Under-spending in a reactive environment leads to asset degradation.",
    objectives: "Compare maintenance expenditures normalized for plant size and value against benchmarks."
  },

  // ── MANUFACTURING PROCESS RELIABILITY ──────────────────────────
  {
    id: "oee",
    categoryId: "process",
    name: "OEE",
    fullName: "Overall Equipment Effectiveness",
    definition: "A composite measure of equipment or asset system performance based on actual availability, performance efficiency, and quality of output when the asset is scheduled to operate.",
    formula: "Availability (%) × Performance Efficiency (%) × Quality Rate (%)",
    formulaDisplay: "Avail% × Perf% × Qual%",
    inputs: [
      { id: "avail", label: "Availability", unit: "%" },
      { id: "perf", label: "Performance", unit: "%" },
      { id: "qual", label: "Quality", unit: "%" },
    ],
    calc: (v) => ({ value: (v.avail/100)*(v.perf/100)*(v.qual/100)*100, label: "%", format: "percent1" }),
    benchmark: { target: "> 85%", low: 85, high: 100, type: "higher", unit: "%" },
    insight: "Identifies major losses (Availability, Speed, Quality). Best used for specific equipment or lines rather than plant-wide. It is a lagging KPI often used in Lean programs.",
    objectives: "Identify and categorize major losses to set improvement priorities and justify root cause analysis."
  },
  {
    id: "teep",
    categoryId: "process",
    name: "TEEP",
    fullName: "Total Effective Equipment Performance",
    definition: "Global measure of equipment performance based on calendar hours (24/7). It measures how well an organization extracts value from its assets based on actual utilization, availability, performance, and quality.",
    formula: "OEE × Utilization",
    formulaDisplay: "OEE × Utilization",
    inputs: [
      { id: "oee", label: "OEE", unit: "%" },
      { id: "util", label: "Utilization", unit: "%" },
    ],
    calc: (v) => ({ value: (v.oee/100)*(v.util/100)*100, label: "%", format: "percent1" }),
    benchmark: { target: "40-50% (Typical)", type: "none" },
    insight: "Indicates the true capacity of the manufacturing operation. Highly variable by industry. Provides a basis for understanding total asset utilization potential.",
    objectives: "Measure how well the organization extracts value from its assets over all calendar hours."
  },
  {
    id: "availability",
    categoryId: "process",
    name: "Availability",
    fullName: "Availability Percentage",
    definition: "The percentage of time that the asset is actually operating (uptime) compared to when it is scheduled to operate. Also called operational availability.",
    formula: "[Uptime / (Total Available Time - Idle Time)] × 100",
    formulaDisplay: "Uptime / (Available - Idle) × 100",
    inputs: [
      { id: "uptime", label: "Uptime", unit: "hrs" },
      { id: "total", label: "Total Available Time", unit: "hrs" },
      { id: "idle", label: "Idle Time", unit: "hrs" },
    ],
    calc: (v) => (v.total - v.idle) > 0 ? { value: (v.uptime / (v.total - v.idle)) * 100, label: "%", format: "percent2" } : null,
    benchmark: { target: "> 95-98%", low: 95, high: 100, type: "higher", unit: "%" },
    insight: "Drivers for availability targets can be raw product availability, market sales, or spare capacity. >98% for continuous, >95% for batch processing.",
    objectives: "Measure when an asset is running or capable of performing its intended function."
  },
  {
    id: "uptime",
    categoryId: "process",
    name: "Uptime",
    fullName: "Uptime Hours",
    definition: "The amount of time an asset is actively producing a product or providing a service. It is the actual running time.",
    formula: "Total Available Time - (Idle Time + Total Downtime)",
    formulaDisplay: "Total - (Idle + Downtime)",
    inputs: [
      { id: "total", label: "Total Available Time", unit: "hrs" },
      { id: "idle", label: "Idle Time", unit: "hrs" },
      { id: "downtime", label: "Total Downtime", unit: "hrs" },
    ],
    calc: (v) => ({ value: v.total - (v.idle + v.downtime), label: "hours", format: "decimal1" }),
    benchmark: { target: "> 98% (Continuous)", type: "none" },
    insight: "Key component of availability. Target >98% for continuous processing, >95% for batch.",
    objectives: "Evaluate the total time the asset has been capable of running to produce product."
  },
  {
    id: "idle-time",
    categoryId: "process",
    name: "Idle Time",
    fullName: "Idle Time Percentage",
    definition: "The amount of time an asset is idle or waiting to run. It includes 'No Demand' and 'Administrative Idle Time'. Does not include downtime.",
    formula: "(Idle Time Hours / Total Available Time) × 100",
    formulaDisplay: "Idle / Total × 100",
    inputs: [
      { id: "idle", label: "Idle Time", unit: "hrs" },
      { id: "total", label: "Total Available Time", unit: "hrs" },
    ],
    calc: (v) => v.total > 0 ? { value: (v.idle / v.total) * 100, label: "%", format: "percent1" } : null,
    benchmark: { target: "Minimize", type: "lower", low: 0, high: 5, unit: "%" },
    insight: "Idle time represents capacity that has been paid for but is not being used. While often beyond plant control (market demand), less is better.",
    objectives: "Identify reasons for a loss in potential capacity due to no demand or admin reasons."
  },
  {
    id: "utilization-time",
    categoryId: "process",
    name: "Utilization Time",
    fullName: "Utilization Time Percentage",
    definition: "The percent of total time that an asset is scheduled to operate during a given time period (usually one year).",
    formula: "[(Total Available - Idle) / Total Available] × 100",
    formulaDisplay: "(Available - Idle) / Available × 100",
    inputs: [
      { id: "total", label: "Total Available Time", unit: "hrs" },
      { id: "idle", label: "Idle Time", unit: "hrs" },
    ],
    calc: (v) => v.total > 0 ? { value: ((v.total - v.idle) / v.total) * 100, label: "%", format: "percent1" } : null,
    benchmark: { target: "Market Driven", type: "none" },
    insight: "Impacted by market demand, raw materials, and labor availability. Valuable for understanding true capacity.",
    objectives: "Assess the amount of time an asset is intended to be in service."
  },

  // ── EQUIPMENT RELIABILITY ──────────────────────────────────────
  {
    id: "criticality-coverage",
    categoryId: "equipment",
    name: "Criticality Coverage",
    fullName: "Systems Covered by Criticality Analysis",
    definition: "The ratio of the number of systems in a facility for which a criticality analysis has been performed divided by the total number of systems.",
    formula: "(Critical Systems Analyzed / Total Systems) × 100",
    formulaDisplay: "Analyzed / Total × 100",
    inputs: [
      { id: "analyzed", label: "Analyzed Systems", unit: "count" },
      { id: "total", label: "Total Systems", unit: "count" },
    ],
    calc: (v) => v.total > 0 ? { value: (v.analyzed / v.total) * 100, label: "%", format: "percent1" } : null,
    benchmark: { target: "100%", low: 99, high: 100, type: "higher", unit: "%" },
    insight: "Foundation for prioritizing all maintenance work. Focuses attention on systems with the most serious consequences of failure.",
    objectives: "Ensure critical systems are identified to prioritize maintenance efforts."
  },
  {
    id: "total-downtime",
    categoryId: "equipment",
    name: "Total Downtime",
    fullName: "Total Downtime",
    definition: "The amount of time an asset is not capable of running. It is the sum of scheduled downtime and unscheduled downtime.",
    formula: "Scheduled Downtime + Unscheduled Downtime",
    formulaDisplay: "Scheduled + Unscheduled",
    inputs: [
      { id: "sched", label: "Scheduled Downtime", unit: "hrs" },
      { id: "unsched", label: "Unscheduled Downtime", unit: "hrs" },
    ],
    calc: (v) => ({ value: v.sched + v.unsched, label: "hours", format: "decimal1" }),
    benchmark: { target: "Minimize", type: "none" },
    insight: "Total non-productive time. Continuous processes typically experience less maintenance-related downtime than batch processes.",
    objectives: "Identify problem areas and potential capacity to minimize downtime."
  },
  {
    id: "scheduled-downtime",
    categoryId: "equipment",
    name: "Scheduled Downtime",
    fullName: "Scheduled Downtime",
    definition: "The amount of time an asset is not capable of running due to scheduled work (e.g., work identified on the finalized weekly schedule).",
    formula: "Sum of planned downtime events",
    formulaDisplay: "Sum(Planned Events)",
    inputs: [
      { id: "events", label: "Sum of Scheduled Events", unit: "hrs" },
    ],
    calc: (v) => ({ value: v.events, label: "hours", format: "decimal1" }),
    benchmark: { target: "Optimize", type: "none" },
    insight: "Necessary investment for reliability. Used to understand the impact of scheduled work on capacity.",
    objectives: "Evaluation of the total amount of time the asset has not been capable of running due to scheduled work."
  },
  {
    id: "unscheduled-downtime",
    categoryId: "equipment",
    name: "Unscheduled Downtime",
    fullName: "Unscheduled Downtime",
    definition: "The amount of time an asset is not capable of running due to unscheduled repairs (e.g., repairs not on the finalized weekly schedule).",
    formula: "Sum of unplanned downtime events",
    formulaDisplay: "Sum(Unplanned Events)",
    inputs: [
      { id: "events", label: "Sum of Unscheduled Events", unit: "hrs" },
    ],
    calc: (v) => ({ value: v.events, label: "hours", format: "decimal1" }),
    benchmark: { target: "< 20% of Total", type: "lower" },
    insight: "Major destroyer of value. Target is typically <20% of total downtime.",
    objectives: "Understand the impact of unscheduled work on capacity and maintenance productivity."
  },
  {
    id: "mtbf",
    categoryId: "equipment",
    name: "MTBF",
    fullName: "Mean Time Between Failures",
    definition: "The average length of operating time between failures for a repairable asset or component. It is the reciprocal of the failure rate at constant rates.",
    formula: "Operating Time / Number of Failures",
    formulaDisplay: "Op Time / Failures",
    inputs: [
      { id: "time", label: "Operating Time", unit: "hrs" },
      { id: "fail", label: "Number of Failures", unit: "count" },
    ],
    calc: (v) => v.fail > 0 ? { value: v.time / v.fail, label: "hours", format: "decimal1" } : null,
    benchmark: { target: "Increasing Trend", type: "trend" },
    insight: "Primary measure of reliability growth for repairable assets. An increasing trend indicates improved asset reliability.",
    objectives: "Assess the reliability of a repairable asset or component."
  },
  {
    id: "mttr",
    categoryId: "equipment",
    name: "MTTR",
    fullName: "Mean Time to Repair",
    definition: "The average time needed to restore an asset to its full operational capabilities after a loss of function. A measure of asset maintainability.",
    formula: "Total Repair Time / Number of Repairs",
    formulaDisplay: "Repair Time / # Repairs",
    inputs: [
      { id: "time", label: "Total Repair Time", unit: "hrs" },
      { id: "repairs", label: "Number of Repairs", unit: "count" },
    ],
    calc: (v) => v.repairs > 0 ? { value: v.time / v.repairs, label: "hours", format: "decimal1" } : null,
    benchmark: { target: "Minimize", type: "lower", low: 0, high: 5, unit: "hrs" },
    insight: "Reflects more than just wrench time; includes diagnosis, testing, and parts acquisition. Best class is often <4 hours for critical assets.",
    objectives: "Assess the maintainability of an asset or system."
  },
  {
    id: "mtbm",
    categoryId: "equipment",
    name: "MTBM",
    fullName: "Mean Time Between Maintenance",
    definition: "The average length of operating time between any maintenance actions (corrective or preventive) that result in function interruption.",
    formula: "Operating Time / Number of Maintenance Actions",
    formulaDisplay: "Op Time / # Actions",
    inputs: [
      { id: "time", label: "Operating Time", unit: "hrs" },
      { id: "actions", label: "Total Maint. Actions", unit: "count" },
    ],
    calc: (v) => v.actions > 0 ? { value: v.time / v.actions, label: "hours", format: "decimal1" } : null,
    benchmark: { target: "Trend", type: "trend" },
    insight: "Includes PMs, so typically lower than MTBF. Used to monitor impact of reliability improvements on extending time between interruptions.",
    objectives: "Measure effectiveness of the maintenance strategy."
  },
  {
    id: "mdt",
    categoryId: "equipment",
    name: "MDT",
    fullName: "Mean Downtime",
    definition: "The average downtime required to restore an asset to its full operational capabilities, including time from failure to restoration (lockout, cleaning, etc.).",
    formula: "Total Downtime / Number of Downtime Events",
    formulaDisplay: "Total Downtime / # Events",
    inputs: [
      { id: "downtime", label: "Total Downtime", unit: "hrs" },
      { id: "events", label: "Downtime Events", unit: "count" },
    ],
    calc: (v) => v.events > 0 ? { value: v.downtime / v.events, label: "hours", format: "decimal1" } : null,
    benchmark: { target: "Minimize", type: "lower" },
    insight: "Includes delays and waiting time. Target <4 hours for critical assets. Optimize maintenance productivity by minimizing repair time.",
    objectives: "Measure effectiveness of the repair strategy and optimize productivity."
  },
  {
    id: "mttf",
    categoryId: "equipment",
    name: "MTTF",
    fullName: "Mean Time To Failure",
    definition: "The average length of operating time to failure for non-repairable assets or components (e.g., light bulbs, belts).",
    formula: "Total Op Time / Number of Failures",
    formulaDisplay: "Op Time / Failures",
    inputs: [
      { id: "time", label: "Total Operating Time", unit: "hrs" },
      { id: "fail", label: "Number of Failures", unit: "count" },
    ],
    calc: (v) => v.fail > 0 ? { value: v.time / v.fail, label: "hours", format: "decimal1" } : null,
    benchmark: { target: "Increasing Trend", type: "trend" },
    insight: "Used for components like belts, bulbs, etc. Best used when focused on component level. Higher MTTF indicates higher reliability.",
    objectives: "Assess reliability of non-repairable assets to predict component life."
  },

  // ── ORGANIZATION & LEADERSHIP ──────────────────────────────────
  {
    id: "rework",
    categoryId: "leadership",
    name: "Rework",
    fullName: "Rework Percentage",
    definition: "Corrective work done on previously maintained equipment that has prematurely failed due to maintenance, operations, or material quality issues.",
    formula: "(Rework Hours / Total Maintenance Hours) × 100",
    formulaDisplay: "Rework / Total × 100",
    inputs: [
      { id: "rework", label: "Rework Hours", unit: "hrs" },
      { id: "total", label: "Total Maint Hours", unit: "hrs" },
    ],
    calc: (v) => v.total > 0 ? { value: (v.rework / v.total) * 100, label: "%", format: "percent2" } : null,
    benchmark: { target: "< 2%", low: 0, high: 2, type: "lower", unit: "%" },
    insight: "Indicator of competence, training, or parts quality. Strategies to reduce include training and improved procedures.",
    objectives: "Identify and measure work resulting from premature failures to eliminate defects."
  },
  {
    id: "training-cost",
    categoryId: "leadership",
    name: "Training Cost",
    fullName: "Maintenance Training Cost per Employee",
    definition: "The cost for formal training that internal maintenance employees receive annually, expressed as cost per employee.",
    formula: "Total Training Cost / Number of Employees",
    formulaDisplay: "Cost / Employees",
    inputs: [
      { id: "cost", label: "Total Training Cost", unit: "$" },
      { id: "emp", label: "Number of Employees", unit: "count" },
    ],
    calc: (v) => v.emp > 0 ? { value: v.cost / v.emp, label: "$ per Employee", format: "currency" } : null,
    benchmark: { target: "$600 - $2000", low: 600, high: 2000, type: "range", unit: "$" },
    insight: "Investment in human capital. Best practice varies by skill level and equipment complexity.",
    objectives: "Measure and trend investment in the skills of internal maintenance employees."
  },
  {
    id: "training-hours",
    categoryId: "leadership",
    name: "Training Hours",
    fullName: "Maintenance Training Hours per Employee",
    definition: "The number of hours of formal training that maintenance personnel receive annually, expressed as hours per employee.",
    formula: "Total Training Hours / Number of Employees",
    formulaDisplay: "Hours / Employees",
    inputs: [
      { id: "hrs", label: "Total Training Hours", unit: "hrs" },
      { id: "emp", label: "Number of Employees", unit: "count" },
    ],
    calc: (v) => v.emp > 0 ? { value: v.hrs / v.emp, label: "hours/emp", format: "decimal1" } : null,
    benchmark: { target: "> 40 hrs/yr", low: 40, high: 100, type: "higher", unit: "hrs" },
    insight: "World class is ~80 hours (2 weeks) per year. Does not include safety training.",
    objectives: "Measure investment in technical training to improve skills."
  },
  {
    id: "training-roi",
    categoryId: "leadership",
    name: "Training ROI",
    fullName: "Maintenance Training ROI",
    definition: "The ratio of the benefit to the cost of training internal maintenance employees.",
    formula: "[(Benefits - Cost) / Cost] × 100",
    formulaDisplay: "(Benefits - Cost) / Cost × 100",
    inputs: [
      { id: "benefit", label: "Business Benefits", unit: "$" },
      { id: "cost", label: "Training Cost", unit: "$" },
    ],
    calc: (v) => v.cost > 0 ? { value: ((v.benefit - v.cost) / v.cost) * 100, label: "%", format: "percent1" } : null,
    benchmark: { target: "> 0%", type: "higher" },
    insight: "Hard to quantify benefits precisely, but essential for justifying budgets.",
    objectives: "Determine return on investment to justify training expenditures."
  },

  // ── WORK MANAGEMENT ────────────────────────────────────────────
  {
    id: "corrective-cost-pct",
    categoryId: "work",
    name: "Corrective Cost %",
    fullName: "Corrective Maintenance Cost Percentage",
    definition: "Percentage of total maintenance cost spent on corrective work to restore equipment after failure or when failure is imminent.",
    formula: "(Corrective Cost / Total Cost) × 100",
    formulaDisplay: "Corrective / Total × 100",
    inputs: [
      { id: "corr", label: "Corrective Cost", unit: "$" },
      { id: "total", label: "Total Maint Cost", unit: "$" },
    ],
    calc: (v) => v.total > 0 ? { value: (v.corr / v.total) * 100, label: "%", format: "percent1" } : null,
    benchmark: { target: "Minimize", type: "lower" },
    insight: "High corrective cost indicates a reactive culture. Shift spending to proactive activities.",
    objectives: "Quantify financial impact of corrective work and evaluate effectiveness of proactive activities."
  },
  {
    id: "corrective-hours-pct",
    categoryId: "work",
    name: "Corrective Hours %",
    fullName: "Corrective Maintenance Hours Percentage",
    definition: "Percentage of labor hours spent on corrective work to restore equipment after failure.",
    formula: "(Corrective Hours / Total Hours) × 100",
    formulaDisplay: "Corrective / Total × 100",
    inputs: [
      { id: "corr", label: "Corrective Hours", unit: "hrs" },
      { id: "total", label: "Total Hours", unit: "hrs" },
    ],
    calc: (v) => v.total > 0 ? { value: (v.corr / v.total) * 100, label: "%", format: "percent1" } : null,
    benchmark: { target: "< 55%", low: 0, high: 55, type: "lower", unit: "%" },
    insight: "Target <55%. Includes corrective work derived from PM/PdM inspections (approx 15%).",
    objectives: "Quantify labor resource impact of corrective work."
  },
  {
    id: "pm-cost-pct",
    categoryId: "work",
    name: "PM Cost %",
    fullName: "Preventive Maintenance Cost Percentage",
    definition: "Percent of cost spent on fixed interval maintenance tasks regardless of equipment condition.",
    formula: "(PM Cost / Total Cost) × 100",
    formulaDisplay: "PM / Total × 100",
    inputs: [
      { id: "pm", label: "PM Cost", unit: "$" },
      { id: "total", label: "Total Cost", unit: "$" },
    ],
    calc: (v) => v.total > 0 ? { value: (v.pm / v.total) * 100, label: "%", format: "percent1" } : null,
    benchmark: { target: "Align with Strategy", type: "none" },
    insight: "Should align with PM Hours %. Ensure work order system distinguishes PM from other work.",
    objectives: "Quantify financial impact of preventive maintenance tasks."
  },
  {
    id: "pm-hours-pct",
    categoryId: "work",
    name: "PM Hours %",
    fullName: "Preventive Maintenance Hours Percentage",
    definition: "Percent of maintenance labor hours used to perform fixed interval maintenance tasks.",
    formula: "(PM Hours / Total Hours) × 100",
    formulaDisplay: "PM / Total × 100",
    inputs: [
      { id: "pm", label: "PM Hours", unit: "hrs" },
      { id: "total", label: "Total Hours", unit: "hrs" },
    ],
    calc: (v) => v.total > 0 ? { value: (v.pm / v.total) * 100, label: "%", format: "percent1" } : null,
    benchmark: { target: "~15%", type: "range", low: 10, high: 20, unit: "%" },
    insight: "Target ~15%. Too high means over-maintaining; lower is acceptable with robust PdM/CBM.",
    objectives: "Quantify labor resource impact of preventive maintenance tasks."
  },
  {
    id: "pdm-cost-pct",
    categoryId: "work",
    name: "PdM Cost %",
    fullName: "Condition Based Maintenance Cost Percentage",
    definition: "Percent of maintenance cost used to measure and trend equipment conditions to detect problems before failure.",
    formula: "(PdM Cost / Total Cost) × 100",
    formulaDisplay: "PdM / Total × 100",
    inputs: [
      { id: "pdm", label: "PdM Cost", unit: "$" },
      { id: "total", label: "Total Cost", unit: "$" },
    ],
    calc: (v) => v.total > 0 ? { value: (v.pdm / v.total) * 100, label: "%", format: "percent1" } : null,
    benchmark: { target: "Increasing", type: "trend" },
    insight: "Higher is generally better if replacing PM/Corrective. Requires consistent work order coding.",
    objectives: "Track cost of condition based (predictive) maintenance tasks."
  },
  {
    id: "pdm-hours-pct",
    categoryId: "work",
    name: "PdM Hours %",
    fullName: "Condition Based Maintenance Hours Percentage",
    definition: "Percent of maintenance labor hours used to measure and trend equipment conditions.",
    formula: "(PdM Hours / Total Hours) × 100",
    formulaDisplay: "PdM / Total × 100",
    inputs: [
      { id: "pdm", label: "PdM Hours", unit: "hrs" },
      { id: "total", label: "Total Hours", unit: "hrs" },
    ],
    calc: (v) => v.total > 0 ? { value: (v.pdm / v.total) * 100, label: "%", format: "percent1" } : null,
    benchmark: { target: "> 15%", type: "higher", low: 15, high: 100, unit: "%" },
    insight: "Target 15% for advanced programs. Higher value acceptable for newer, advanced equipment strategies.",
    objectives: "Quantify labor resource impact of condition based maintenance."
  },
  {
    id: "shutdown-cost-pct",
    categoryId: "work",
    name: "Shutdown Cost %",
    fullName: "Maintenance Shutdown Cost Percentage",
    definition: "Percent of annual maintenance cost spent on planned maintenance shutdowns/turnarounds.",
    formula: "(Shutdown Cost / Total Cost) × 100",
    formulaDisplay: "Shutdown / Total × 100",
    inputs: [
      { id: "shut", label: "Shutdown Cost", unit: "$" },
      { id: "total", label: "Total Cost", unit: "$" },
    ],
    calc: (v) => v.total > 0 ? { value: (v.shut / v.total) * 100, label: "%", format: "percent1" } : null,
    benchmark: { target: "Varies by Industry", type: "none" },
    insight: "High for process industries, low for discrete. Used for zero-based budgeting and cost reduction analysis.",
    objectives: "Track contribution of planned shutdown cost to total maintenance cost."
  },
  {
    id: "planned-work-pct",
    categoryId: "work",
    name: "Planned Work %",
    fullName: "Planned Work Percentage",
    definition: "Hours spent on planned work vs total maintenance labor hours. Planned work is identified by the planner before execution.",
    formula: "(Planned Hours / Total Hours) × 100",
    formulaDisplay: "Planned / Total × 100",
    inputs: [
      { id: "plan", label: "Planned Hours", unit: "hrs" },
      { id: "total", label: "Total Hours", unit: "hrs" },
    ],
    calc: (v) => v.total > 0 ? { value: (v.plan / v.total) * 100, label: "%", format: "percent1" } : null,
    benchmark: { target: "> 90%", low: 90, high: 100, type: "higher", unit: "%" },
    insight: "World class planning execution is >90%. Secondary indicator of craft utilization and wrench time potential.",
    objectives: "Measure the amount of planned work being executed to assess planning process effectiveness."
  },
  {
    id: "unplanned-work-pct",
    categoryId: "work",
    name: "Unplanned Work %",
    fullName: "Unplanned Work Percentage",
    definition: "Hours spent on unplanned work vs total maintenance labor hours. High unplanned work indicates a reactive environment.",
    formula: "(Unplanned Hours / Total Hours) × 100",
    formulaDisplay: "Unplanned / Total × 100",
    inputs: [
      { id: "unplan", label: "Unplanned Hours", unit: "hrs" },
      { id: "total", label: "Total Hours", unit: "hrs" },
    ],
    calc: (v) => v.total > 0 ? { value: (v.unplan / v.total) * 100, label: "%", format: "percent1" } : null,
    benchmark: { target: "< 10%", low: 0, high: 10, type: "lower", unit: "%" },
    insight: "Less than 10% is best-in-class. Inverse of planned work.",
    objectives: "Measure unplanned work to minimize reactive chaos."
  },
  {
    id: "cost-estimate-accuracy",
    categoryId: "work",
    name: "Cost Estimate Accuracy",
    fullName: "Actual Cost to Planning Estimate",
    definition: "The ratio of the actual cost incurred on a work order to the estimated cost for that work order.",
    formula: "(Actual Cost / Planned Cost) × 100",
    formulaDisplay: "Actual / Planned × 100",
    inputs: [
      { id: "actual", label: "Actual Cost", unit: "$" },
      { id: "plan", label: "Planned Cost", unit: "$" },
    ],
    calc: (v) => v.plan > 0 ? { value: (v.actual / v.plan) * 100, label: "%", format: "percent1" } : null,
    benchmark: { target: "90% – 110%", low: 90, high: 110, type: "range", unit: "%" },
    insight: "Best class 1st quartile is +/- 10%. Measures planning accuracy and execution efficiency.",
    objectives: "Measure accuracy of work planning and efficiency of execution."
  },
  {
    id: "hour-estimate-accuracy",
    categoryId: "work",
    name: "Hour Estimate Accuracy",
    fullName: "Actual Hours to Planning Estimate",
    definition: "The ratio of actual labor hours reported on a work order to the estimated labor hours planned.",
    formula: "(Actual Hours / Planned Hours) × 100",
    formulaDisplay: "Actual / Planned × 100",
    inputs: [
      { id: "actual", label: "Actual Hours", unit: "hrs" },
      { id: "plan", label: "Planned Hours", unit: "hrs" },
    ],
    calc: (v) => v.plan > 0 ? { value: (v.actual / v.plan) * 100, label: "%", format: "percent1" } : null,
    benchmark: { target: "90% – 110%", low: 90, high: 110, type: "range", unit: "%" },
    insight: "Target +/- 10%. Critical for effective scheduling.",
    objectives: "Measure accuracy of labor planning and efficiency of execution."
  },
  {
    id: "planning-variance-index",
    categoryId: "work",
    name: "Planning Variance Index",
    fullName: "Planning Variance Index",
    definition: "Percentage of planned work orders closed in which the actual cost varied within +/- 10% of the planned cost.",
    formula: "(WOs within 10% / Total WOs) × 100",
    formulaDisplay: "Accurate WOs / Total WOs × 100",
    inputs: [
      { id: "accurate", label: "WOs within 10% Var", unit: "count" },
      { id: "total", label: "Total Planned WOs", unit: "count" },
    ],
    calc: (v) => v.total > 0 ? { value: (v.accurate / v.total) * 100, label: "%", format: "percent1" } : null,
    benchmark: { target: "Maximize", type: "higher" },
    insight: "Measures consistency of planning. Reflects efficiency of planned work execution.",
    objectives: "Measure accuracy with which work is planned."
  },
  {
    id: "planner-productivity",
    categoryId: "work",
    name: "Planner Productivity",
    fullName: "Planner Productivity",
    definition: "The average amount of planned work a maintenance planner prepares per month (measured in job plans or hours).",
    formula: "Number of Job Plans / Number of Months",
    formulaDisplay: "Plans / Months",
    inputs: [
      { id: "num_plans", label: "Number of Job Plans Created", unit: "count" },
      { id: "num_months", label: "Number of Months", unit: "count" },
    ],
    calc: (v) => v.num_months > 0 ? { value: v.num_plans / v.num_months, label: "plans/month", format: "decimal0" } : null,
    benchmark: { target: "25-35 plans/mo", type: "range" },
    insight: "Range target: 25-35 plans/month. Varies by industry and complexity. Best analyzed alongside planned labor hours.",
    objectives: "Quantify the amount of work planned by the maintenance planner."
  },
  {
    id: "reactive-work",
    categoryId: "work",
    name: "Reactive Work",
    fullName: "Reactive / Break-in Work",
    definition: "Maintenance work that interrupts the weekly schedule, calculated as a percentage of total maintenance labor hours.",
    formula: "(Break-in Hours / Total Hours) × 100",
    formulaDisplay: "Break-in / Total × 100",
    inputs: [
      { id: "break", label: "Break-in Hours", unit: "hrs" },
      { id: "total", label: "Total Hours", unit: "hrs" },
    ],
    calc: (v) => v.total > 0 ? { value: (v.break / v.total) * 100, label: "%", format: "percent1" } : null,
    benchmark: { target: "< 10%", low: 0, high: 10, type: "lower", unit: "%" },
    insight: "Schedule breakers destroy efficiency. Target <10% requires robust proactive maintenance.",
    objectives: "Measure and monitor the amount of work performed outside of the weekly schedule."
  },
  {
    id: "proactive-work",
    categoryId: "work",
    name: "Proactive Work",
    fullName: "Proactive Work Percentage",
    definition: "Maintenance work completed to avoid failures or identify defects. Includes PM, PdM, and resulting corrective work.",
    formula: "(Proactive Hours / Total Hours) × 100",
    formulaDisplay: "Proactive / Total × 100",
    inputs: [
      { id: "pro", label: "Proactive Hours", unit: "hrs" },
      { id: "total", label: "Total Hours", unit: "hrs" },
    ],
    calc: (v) => v.total > 0 ? { value: (v.pro / v.total) * 100, label: "%", format: "percent1" } : null,
    benchmark: { target: "> 80%", low: 80, high: 100, type: "higher", unit: "%" },
    insight: "Includes PM/PdM + planned corrective. Shift culture to proactive.",
    objectives: "Measure work done to prevent failures or identify defects."
  },
  {
    id: "schedule-compliance",
    categoryId: "work",
    name: "Schedule Compliance",
    fullName: "Schedule Compliance (Hours)",
    definition: "A measure of adherence to the maintenance schedule, expressed as a percent of total time available to schedule.",
    formula: "(Completed Sched Hours / Total Sched Hours) × 100",
    formulaDisplay: "Completed / Scheduled × 100",
    inputs: [
      { id: "comp", label: "Completed Sched Hrs", unit: "hrs" },
      { id: "total", label: "Total Sched Hrs", unit: "hrs" },
    ],
    calc: (v) => v.total > 0 ? { value: (v.comp / v.total) * 100, label: "%", format: "percent1" } : null,
    benchmark: { target: "> 90%", low: 90, high: 100, type: "higher", unit: "%" },
    insight: "Target >90%. Requires 100% of available hours to be scheduled for accuracy.",
    objectives: "Measure compliance to the maintenance schedule and effectiveness of the scheduling process."
  },
  {
    id: "standing-wos",
    categoryId: "work",
    name: "Standing WOs",
    fullName: "Standing Work Order Percentage",
    definition: "The ratio of hours worked on standing work orders to the total maintenance labor hours.",
    formula: "(Standing WO Hours / Total Hours) × 100",
    formulaDisplay: "Standing / Total × 100",
    inputs: [
      { id: "stand", label: "Standing WO Hours", unit: "hrs" },
      { id: "total", label: "Total Hours", unit: "hrs" },
    ],
    calc: (v) => v.total > 0 ? { value: (v.stand / v.total) * 100, label: "%", format: "percent1" } : null,
    benchmark: { target: "< 5%", low: 0, high: 5, type: "lower", unit: "%" },
    insight: "Best-in-class <5%. Standing WOs hide specific asset history and should be minimized.",
    objectives: "Measure amount of maintenance work charged to standing orders."
  },
  {
    id: "wo-aging",
    categoryId: "work",
    name: "WO Aging",
    fullName: "Work Order Aging",
    definition: "Measures the age of active work orders by comparing creation date to today's date.",
    formula: "Current Date - Creation Date",
    formulaDisplay: "Current - Creation",
    inputs: [
      { id: "current", label: "Current Julian Date", unit: "day" },
      { id: "create", label: "Creation Julian Date", unit: "day" },
    ],
    calc: (v) => ({ value: v.current - v.create, label: "days", format: "decimal0" }),
    benchmark: { target: "Monitor Buckets", type: "none" },
    insight: "Track in buckets (0-30, 31-90, 90+). Helps ensure backlog management.",
    objectives: "Track work order aging to ensure effective backlog management."
  },
  {
    id: "wo-cycle-time",
    categoryId: "work",
    name: "WO Cycle Time",
    fullName: "Work Order Cycle Time",
    definition: "The time from the creation of an individual work order until it is closed in the system.",
    formula: "Completion Date - Creation Date",
    formulaDisplay: "Complete - Create",
    inputs: [
      { id: "comp", label: "Completion Julian Date", unit: "day" },
      { id: "create", label: "Creation Julian Date", unit: "day" },
    ],
    calc: (v) => ({ value: v.comp - v.create, label: "days", format: "decimal0" }),
    benchmark: { target: "10-30 days", type: "range" },
    insight: "Target 10-30 days for planned work. Long cycle times require follow-up.",
    objectives: "Measure how long it takes to complete work from creation to completion."
  },
  {
    id: "planned-backlog",
    categoryId: "work",
    name: "Planned Backlog",
    fullName: "Planned Backlog Weeks",
    definition: "The combination of fully planned work (not ready) and ready work available, expressed in weeks of crew capacity.",
    formula: "(Planned + Ready Hours) / Weekly Capacity",
    formulaDisplay: "(Planned + Ready) / Capacity",
    inputs: [
      { id: "planned_backlog_hrs", label: "Planned Work Hours", unit: "hrs" },
      { id: "ready_backlog_hrs", label: "Ready Work Hours", unit: "hrs" },
      { id: "weekly_crew_capacity", label: "Weekly Crew Capacity", unit: "hrs/wk" },
    ],
    calc: (v) => v.weekly_crew_capacity > 0 ? { value: (v.planned_backlog_hrs + v.ready_backlog_hrs) / v.weekly_crew_capacity, label: "weeks", format: "decimal1" } : null,
    benchmark: { target: "4-6 weeks", low: 4, high: 6, type: "range", unit: "wks" },
    insight: "Target 4-6 weeks total backlog. Ensures steady work and balances staffing.",
    objectives: "Measure quantity of work to be performed to balance labor resources."
  },
  {
    id: "ready-backlog",
    categoryId: "work",
    name: "Ready Backlog",
    fullName: "Ready Backlog Weeks",
    definition: "Work that has been fully prepared (planned and materials procured) but not yet scheduled.",
    formula: "Ready Hours / Weekly Capacity",
    formulaDisplay: "Ready Hours / Capacity",
    inputs: [
      { id: "ready_work_hrs", label: "Ready Work Hours", unit: "hrs" },
      { id: "weekly_crew_capacity", label: "Weekly Crew Capacity", unit: "hrs/wk" },
    ],
    calc: (v) => v.weekly_crew_capacity > 0 ? { value: v.ready_work_hrs / v.weekly_crew_capacity, label: "weeks", format: "decimal1" } : null,
    benchmark: { target: "2-4 weeks", low: 2, high: 4, type: "range", unit: "wks" },
    insight: "Target 2-4 weeks. Buffer for the weekly schedule.",
    objectives: "Measure quantity of ready work to ensure schedulable work availability."
  },
  {
    id: "pm-pdm-compliance",
    categoryId: "work",
    name: "PM/PdM Compliance",
    fullName: "PM & PdM Work Order Compliance",
    definition: "Percentage of PM/PdM work orders completed past expected date. Calculates variance of actual execution vs planned frequency.",
    formula: "([Actual Freq / Planned Freq] * 100) - 100",
    formulaDisplay: "(Actual / Planned * 100) - 100",
    inputs: [
      { id: "actual", label: "Actual Frequency", unit: "days" },
      { id: "plan", label: "Planned Frequency", unit: "days" },
    ],
    calc: (v) => v.plan > 0 ? { value: ((v.actual / v.plan) * 100) - 100, label: "% Variance", format: "decimal1" } : null,
    benchmark: { target: "0%", type: "none" },
    insight: "Measures interval drift. Objective is to capture and trend completion information.",
    objectives: "Capture and trend PM/PdM completion to ensure assets are managed by criticality."
  },
  {
    id: "pm-pdm-overdue",
    categoryId: "work",
    name: "PM/PdM Overdue",
    fullName: "PM & PdM Work Orders Overdue",
    definition: "Measures all active PM/PdM work orders not completed by due date.",
    formula: "(Overdue PMs / Total PMs) × 100",
    formulaDisplay: "Overdue / Total × 100",
    inputs: [
      { id: "over", label: "Overdue PMs", unit: "count" },
      { id: "total", label: "Total PMs", unit: "count" },
    ],
    calc: (v) => v.total > 0 ? { value: (v.over / v.total) * 100, label: "%", format: "percent1" } : null,
    benchmark: { target: "< 5%", low: 0, high: 5, type: "lower", unit: "%" },
    insight: "Target <5%. Goal is to finish work in 2-4 weeks. Prevents perceived lack of responsiveness.",
    objectives: "Review backlog and develop plans to resolve overdue tasks."
  },
  {
    id: "pm-pdm-yield",
    categoryId: "work",
    name: "PM/PdM Yield",
    fullName: "PM & PdM Yield",
    definition: "Ratio of corrective work generated from PM/PdM inspections to the amount of PM/PdM work done.",
    formula: "Corrective from PM / PM Hours",
    formulaDisplay: "Corrective Hrs / PM Hrs",
    inputs: [
      { id: "corr", label: "Corrective from PM", unit: "hrs" },
      { id: "pm", label: "PM Hours", unit: "hrs" },
    ],
    calc: (v) => v.pm > 0 ? { value: v.corr / v.pm, label: "Ratio", format: "decimal2" } : null,
    benchmark: { target: "Trend", type: "none" },
    insight: "Measures if PMs are finding problems. Varies by plant.",
    objectives: "Measure corrective work generated by PM/PdM to validate effectiveness."
  },
  {
    id: "pm-effectiveness",
    categoryId: "work",
    name: "PM Effectiveness",
    fullName: "PM & PdM Effectiveness",
    definition: "Ratio of necessary repairs vs total repairs written from PM/PdM. Measures effectiveness of corrective work from proactive strategies.",
    formula: "Necessary Repairs / Total Written",
    formulaDisplay: "Necessary / Written",
    inputs: [
      { id: "nec", label: "Necessary Repairs", unit: "count" },
      { id: "writ", label: "Written Repairs", unit: "count" },
    ],
    calc: (v) => v.writ > 0 ? { value: v.nec / v.writ, label: "Ratio", format: "decimal2" } : null,
    benchmark: { target: "High", type: "higher" },
    insight: "Identifies unnecessary tasks. Used to optimize PM/PdM programs.",
    objectives: "Measure effectiveness of PM/PdM at reducing failure risk and identify unnecessary tasks."
  },
  {
    id: "pm-pdm-schedule-comp",
    categoryId: "work",
    name: "PM/PdM Schedule Comp",
    fullName: "PM & PdM Schedule Compliance",
    definition: "Review of completed PM/PdM work orders against preset criteria (e.g., by due date).",
    formula: "(PMs on Time / PMs Due) × 100",
    formulaDisplay: "On Time / Due × 100",
    inputs: [
      { id: "ontime", label: "PMs On Time", unit: "count" },
      { id: "due", label: "PMs Due", unit: "count" },
    ],
    calc: (v) => v.due > 0 ? { value: (v.ontime / v.due) * 100, label: "%", format: "percent1" } : null,
    benchmark: { target: "> 90%", low: 90, high: 100, type: "higher", unit: "%" },
    insight: "Target >90%. Strict compliance prevents failures. If completed early, reschedule next from completion date.",
    objectives: "Summarize PM and PdM work order execution and completion compliance."
  },
  {
    id: "craft-to-supervisor",
    categoryId: "work",
    name: "Craft to Supervisor",
    fullName: "Craft Worker to Supervisor Ratio",
    definition: "The ratio of maintenance craft workers to supervisors.",
    formula: "Craft Workers / Supervisors",
    formulaDisplay: "Craft / Supervisors",
    inputs: [
      { id: "craft", label: "Craft Workers", unit: "count" },
      { id: "sup", label: "Supervisors", unit: "count" },
    ],
    calc: (v) => v.sup > 0 ? { value: v.craft / v.sup, label: ": 1", format: "decimal0" } : null,
    benchmark: { target: "12:1 to 15:1", type: "range" },
    insight: "Best class (1st Quartile) is 12:1. Second Quartile is 15:1. Include contractors if they report to supervisor.",
    objectives: "Measure manpower workload of supervisors for benchmarking."
  },
  {
    id: "craft-to-planner",
    categoryId: "work",
    name: "Craft to Planner",
    fullName: "Craft Worker to Planner Ratio",
    definition: "The ratio of maintenance craft workers to planners.",
    formula: "Craft Workers / Planners",
    formulaDisplay: "Craft / Planners",
    inputs: [
      { id: "craft", label: "Craft Workers", unit: "count" },
      { id: "plan", label: "Planners", unit: "count" },
    ],
    calc: (v) => v.plan > 0 ? { value: v.craft / v.plan, label: ": 1", format: "decimal0" } : null,
    benchmark: { target: "20:1", type: "threshold" },
    insight: "Target 20:1. Identifies level of planning activity necessary to maintain backlog.",
    objectives: "Measure manpower planning workload of planners."
  },
  {
    id: "direct-to-indirect",
    categoryId: "work",
    name: "Direct to Indirect",
    fullName: "Direct to Indirect Maintenance Ratio",
    definition: "Ratio of direct (hands-on) to indirect (support) maintenance personnel.",
    formula: "Direct Personnel / Indirect Personnel",
    formulaDisplay: "Direct / Indirect",
    inputs: [
      { id: "dir", label: "Direct Personnel", unit: "count" },
      { id: "indir", label: "Indirect Personnel", unit: "count" },
    ],
    calc: (v) => v.indir > 0 ? { value: v.dir / v.indir, label: ": 1", format: "decimal1" } : null,
    benchmark: { target: "2:1 to 3:1", type: "range" },
    insight: "Target 2:1 to 3:1. Higher ratio achievable for technically advanced facilities.",
    objectives: "Analyze balance of direct and indirect personnel for staffing management."
  },
  {
    id: "indirect-cost-pct",
    categoryId: "work",
    name: "Indirect Cost %",
    fullName: "Indirect Maintenance Personnel Cost",
    definition: "Cost incurred for indirect maintenance personnel expressed as a percentage of total maintenance cost.",
    formula: "(Indirect Cost / Total Cost) × 100",
    formulaDisplay: "Indirect / Total × 100",
    inputs: [
      { id: "ind", label: "Indirect Cost", unit: "$" },
      { id: "total", label: "Total Cost", unit: "$" },
    ],
    calc: (v) => v.total > 0 ? { value: (v.ind / v.total) * 100, label: "%", format: "percent1" } : null,
    benchmark: { target: "Monitor", type: "none" },
    insight: "Measures contribution of indirect labor to total cost. Compare to industry benchmarks.",
    objectives: "Measure contribution of indirect maintenance labor cost."
  },
  {
    id: "internal-labor-cost-pct",
    categoryId: "work",
    name: "Internal Labor Cost %",
    fullName: "Internal Maintenance Employee Cost",
    definition: "Total burdened cost for plant maintenance employees expressed as a percentage of total maintenance cost.",
    formula: "(Internal Labor Cost / Total Cost) × 100",
    formulaDisplay: "Internal / Total × 100",
    inputs: [
      { id: "int", label: "Internal Labor Cost", unit: "$" },
      { id: "total", label: "Total Cost", unit: "$" },
    ],
    calc: (v) => v.total > 0 ? { value: (v.int / v.total) * 100, label: "%", format: "percent1" } : null,
    benchmark: { target: "Strategy Dependent", type: "none" },
    insight: "Use cautiously for comparison. Depends on insource/outsource strategy.",
    objectives: "Monitor relationship of maintenance labor costs to total costs."
  },
  {
    id: "shift-ratio",
    categoryId: "work",
    name: "Shift Ratio",
    fullName: "Craft Worker on Shift Ratio",
    definition: "Ratio of maintenance craft workers on shift (responding to unexpected failures) versus total craft workers.",
    formula: "Shift Workers / Total Workers",
    formulaDisplay: "Shift / Total",
    inputs: [
      { id: "shift", label: "Shift Workers", unit: "count" },
      { id: "total", label: "Total Workers", unit: "count" },
    ],
    calc: (v) => v.total > 0 ? { value: v.shift / v.total, label: "Ratio", format: "decimal2" } : null,
    benchmark: { target: "Minimize", type: "none" },
    insight: "Indirect measurement of reliability. Frequent unexpected failures require more shift workers.",
    objectives: "Trend shift work to identify maintenance issues."
  },
  {
    id: "overtime-cost-pct",
    categoryId: "work",
    name: "Overtime Cost %",
    fullName: "Overtime Maintenance Cost Percentage",
    definition: "Cost of overtime maintenance labor divided by total maintenance labor cost.",
    formula: "(Overtime Cost / Total Labor Cost) × 100",
    formulaDisplay: "Overtime / Total × 100",
    inputs: [
      { id: "ot", label: "Overtime Cost", unit: "$" },
      { id: "total", label: "Total Labor Cost", unit: "$" },
    ],
    calc: (v) => v.total > 0 ? { value: (v.ot / v.total) * 100, label: "%", format: "percent1" } : null,
    benchmark: { target: "< 5%", low: 0, high: 5, type: "lower", unit: "%" },
    insight: "Target <5%. High OT cost indicates staffing issues or inefficiency.",
    objectives: "Determine if workforce is performing effectively and appropriately staffed."
  },
  {
    id: "overtime-hours-pct",
    categoryId: "work",
    name: "Overtime Hours %",
    fullName: "Overtime Maintenance Hours Percentage",
    definition: "Number of overtime maintenance labor hours divided by total maintenance labor hours.",
    formula: "(Overtime Hours / Total Hours) × 100",
    formulaDisplay: "Overtime / Total × 100",
    inputs: [
      { id: "ot", label: "Overtime Hours", unit: "hrs" },
      { id: "total", label: "Total Hours", unit: "hrs" },
    ],
    calc: (v) => v.total > 0 ? { value: (v.ot / v.total) * 100, label: "%", format: "percent1" } : null,
    benchmark: { target: "5% - 10%", low: 5, high: 10, type: "range", unit: "%" },
    insight: "Target <10% (with shutdowns) or <5% (routine). High OT can indicate poor performance.",
    objectives: "Determine if workforce is performing effectively and appropriately staffed."
  },
  {
    id: "inventory-turns",
    categoryId: "work",
    name: "Inventory Turns",
    fullName: "Stores Inventory Turns",
    definition: "Measure of how quickly inventory is flowing through the storeroom system.",
    formula: "Stock Purchased / Stock On Hand",
    formulaDisplay: "Purchased / On Hand",
    inputs: [
      { id: "purch", label: "Annual Purchase Value", unit: "$" },
      { id: "hand", label: "Stock On Hand Value", unit: "$" },
    ],
    calc: (v) => v.hand > 0 ? { value: v.purch / v.hand, label: "turns/yr", format: "decimal2" } : null,
    benchmark: { target: "> 1.0", low: 1, high: 3, type: "higher", unit: "turns" },
    insight: "Target >1.0 total, >3.0 without critical spares. Measure of inventory liquidity.",
    objectives: "Measure appropriateness of storeroom inventory levels."
  },
  {
    id: "vmi-pct",
    categoryId: "work",
    name: "VMI %",
    fullName: "Vendor Managed Inventory Percentage",
    definition: "Ratio of number of stocked items managed by vendor to total number of stocked items.",
    formula: "(VMI Items / Total Items) × 100",
    formulaDisplay: "VMI / Total × 100",
    inputs: [
      { id: "vmi", label: "VMI Items", unit: "count" },
      { id: "total", label: "Total Items", unit: "count" },
    ],
    calc: (v) => v.total > 0 ? { value: (v.vmi / v.total) * 100, label: "%", format: "percent1" } : null,
    benchmark: { target: "Strategy Dependent", type: "none" },
    insight: "VMI reduces holding costs but requires oversight (min/max levels).",
    objectives: "Quantify amount of MRO stock that is vendor managed."
  },
  {
    id: "stockouts",
    categoryId: "work",
    name: "Stockouts",
    fullName: "Stockout Percentage",
    definition: "Measure of the frequency that a customer cannot immediately obtain the part needed.",
    formula: "(Stockouts / Total Requests) × 100",
    formulaDisplay: "Stockouts / Total × 100",
    inputs: [
      { id: "out", label: "Number of Stockouts", unit: "count" },
      { id: "req", label: "Total Requests", unit: "count" },
    ],
    calc: (v) => v.req > 0 ? { value: (v.out / v.req) * 100, label: "%", format: "percent1" } : null,
    benchmark: { target: "< 2%", low: 0, high: 2, type: "lower", unit: "%" },
    insight: "Target <2%. High stockouts kill productivity. Too little inventory results in downtime.",
    objectives: "Maintain appropriate balance in stocked inventory."
  },
  {
    id: "inactive-stock",
    categoryId: "work",
    name: "Inactive Stock",
    fullName: "Inactive Stock Percentage",
    definition: "Ratio of inactive MRO inventory stock records to total MRO records (excluding critical spares/non-stock).",
    formula: "(Inactive Records / Total Records) × 100",
    formulaDisplay: "Inactive / Total × 100",
    inputs: [
      { id: "inactive", label: "Inactive Records", unit: "count" },
      { id: "total", label: "Total Records", unit: "count" },
    ],
    calc: (v) => v.total > 0 ? { value: (v.inactive / v.total) * 100, label: "%", format: "percent1" } : null,
    benchmark: { target: "< 1%", low: 0, high: 1, type: "lower", unit: "%" },
    insight: "Target <1%. Inactive stock is dead money. Ensure critical spares are well defined.",
    objectives: "Measure percentage of non-critical stock with no usage to reduce working capital."
  },
  {
    id: "store-transactions",
    categoryId: "work",
    name: "Store Transactions",
    fullName: "Storeroom Transactions",
    definition: "Ratio of total storeroom transactions to total storeroom clerks.",
    formula: "Transactions / Clerks",
    formulaDisplay: "Trans / Clerks",
    inputs: [
      { id: "trans", label: "Total Transactions", unit: "count" },
      { id: "clerks", label: "Number of Clerks", unit: "count" },
    ],
    calc: (v) => v.clerks > 0 ? { value: v.trans / v.clerks, label: "trans/clerk", format: "decimal0" } : null,
    benchmark: { target: "60-140 per day", type: "none" },
    insight: "Target 60-140/day. Assumes use of barcodes/scanning. Measure of workload.",
    objectives: "Measure workload of storeroom clerks for trending."
  },
  {
    id: "store-records",
    categoryId: "work",
    name: "Store Records",
    fullName: "Storeroom Records per Clerk",
    definition: "Ratio of MRO inventory stock records (SKUs) to total storeroom clerks.",
    formula: "Records / Clerks",
    formulaDisplay: "Records / Clerks",
    inputs: [
      { id: "rec", label: "Total Records", unit: "count" },
      { id: "clerks", label: "Number of Clerks", unit: "count" },
    ],
    calc: (v) => v.clerks > 0 ? { value: v.rec / v.clerks, label: "rec/clerk", format: "decimal0" } : null,
    benchmark: { target: "~5000", type: "none" },
    insight: "Target 5,000 per attendant. Affected by organization and technology.",
    objectives: "Measure workload of store clerks for trending."
  },
  {
    id: "material-cost-pct",
    categoryId: "work",
    name: "Material Cost %",
    fullName: "Maintenance Material Cost Percentage",
    definition: "Total cost incurred for materials/supplies expressed as a percentage of total maintenance cost.",
    formula: "(Material Cost / Total Cost) × 100",
    formulaDisplay: "Material / Total × 100",
    inputs: [
      { id: "mat", label: "Material Cost", unit: "$" },
      { id: "total", label: "Total Maintenance Cost", unit: "$" },
    ],
    calc: (v) => v.total > 0 ? { value: (v.mat / v.total) * 100, label: "%", format: "percent1" } : null,
    benchmark: { target: "~50%", type: "none" },
    insight: "Target ~50%. Valid for US/Canada labor rates. Lower labor rates drive this % higher.",
    objectives: "Monitor contribution of material costs to total maintenance costs."
  },
  {
    id: "contractor-cost-pct",
    categoryId: "work",
    name: "Contractor Cost %",
    fullName: "Contractor Maintenance Cost Percentage",
    definition: "Percentage of contractor costs of the total maintenance costs used to maintain assets.",
    formula: "(Contractor Cost / Total Cost) × 100",
    formulaDisplay: "Contractor / Total × 100",
    inputs: [
      { id: "cont", label: "Contractor Cost", unit: "$" },
      { id: "total", label: "Total Cost", unit: "$" },
    ],
    calc: (v) => v.total > 0 ? { value: (v.cont / v.total) * 100, label: "%", format: "percent1" } : null,
    benchmark: { target: "Strategy Dependent", type: "none" },
    insight: "Depends on outsourcing strategy.",
    objectives: "Quantify contractor costs for trending and benchmarking."
  },
  {
    id: "contractor-hours-pct",
    categoryId: "work",
    name: "Contractor Hours %",
    fullName: "Contractor Hours Percentage",
    definition: "Percentage of contractor labor hours out of total maintenance labor hours.",
    formula: "(Contractor Hours / Total Hours) × 100",
    formulaDisplay: "Contractor / Total × 100",
    inputs: [
      { id: "cont", label: "Contractor Hours", unit: "hrs" },
      { id: "total", label: "Total Hours", unit: "hrs" },
    ],
    calc: (v) => v.total > 0 ? { value: (v.cont / v.total) * 100, label: "%", format: "percent1" } : null,
    benchmark: { target: "Strategy Dependent", type: "none" },
    insight: "Highly variable by industry. Measure reliance on external labor.",
    objectives: "Quantify contractor labor hours for trending and benchmarking."
  },
  {
    id: "wrench-time",
    categoryId: "work",
    name: "Wrench Time",
    fullName: "Wrench Time Percentage",
    definition: "Measure of time a craft worker spends applying physical effort or troubleshooting. Measured through work sampling.",
    formula: "(Wrench Obs / Total Obs) × 100",
    formulaDisplay: "Wrench / Total × 100",
    inputs: [
      { id: "wrench", label: "Wrench Observations", unit: "count" },
      { id: "total", label: "Total Observations", unit: "count" },
    ],
    calc: (v) => v.total > 0 ? { value: (v.wrench / v.total) * 100, label: "%", format: "percent1" } : null,
    benchmark: { target: "50% – 55%", low: 50, high: 60, type: "higher", unit: "%" },
    insight: "Target 50-55%. Not achievable without robust planning. Average is often 35%.",
    objectives: "Identify opportunities to increase productivity by quantifying activities."
  },
  {
    id: "ci-hours",
    categoryId: "work",
    name: "CI Hours",
    fullName: "Continuous Improvement Hours",
    definition: "Percentage of labor hours of maintenance and operator employees used on continuous improvement activities.",
    formula: "(CI Hours / Total Hours) × 100",
    formulaDisplay: "CI / Total × 100",
    inputs: [
      { id: "ci", label: "CI Hours", unit: "hrs" },
      { id: "total", label: "Total Hours", unit: "hrs" },
    ],
    calc: (v) => v.total > 0 ? { value: (v.ci / v.total) * 100, label: "%", format: "percent1" } : null,
    benchmark: { target: "> 5%", low: 5, high: 100, type: "higher", unit: "%" },
    insight: "Target >5%. Investment in the future.",
    objectives: "Quantify maintenance labor hours used on continuous improvement activities."
  }
];